import React, { useEffect, useRef } from 'react';
import { useStyletron } from 'baseui';
import { ListItem } from 'baseui/list';
import { HIERARCHY as TAG_HIERARCHY, KIND as TAG_KIND, SIZE as TAG_SIZE, Tag } from 'baseui/tag';
import { PLACEMENT, StatefulTooltip } from 'baseui/tooltip';
import { LabelMedium, LabelXSmall, ParagraphSmall, ParagraphXSmall } from 'baseui/typography';
import usePreviousValue from '../../hooks/usePreviousValue';
import { BatchItemAction } from './batch-item-action';
import type { BatchItemT } from './types';
import { BATCH_ITEM_STATUS } from './types';
const BATCH_ITEM_STATUS_STYLES = ({
  [BATCH_ITEM_STATUS.CURRENT]: {
    kind: TAG_KIND.accent,
    hierarchy: TAG_HIERARCHY.primary
  },
  [BATCH_ITEM_STATUS.NEXT]: {
    kind: TAG_KIND.warning,
    hierarchy: TAG_HIERARCHY.primary
  },
  [BATCH_ITEM_STATUS.PREVIOUS]: {
    kind: TAG_KIND.warning,
    hierarchy: TAG_HIERARCHY.primary
  },
  [BATCH_ITEM_STATUS.PENDING]: {
    kind: TAG_KIND.neutral,
    hierarchy: TAG_HIERARCHY.secondary
  }
} as const);
const tagOverrides = {
  Root: {
    style: ({
      $theme
    }) => ({
      margin: 0,
      padding: $theme.sizing.scale100
    })
  },
  Text: {
    style: ({
      $theme
    }) => ({
      ...$theme.typography.LabelXSmall
    })
  }
};
const MAX_CONTENT_HEIGHT = 300;
type BatchItemProps = {
  expanded?: boolean;
  item: BatchItemT;
  onPlayComplete?: () => void;
  disableFocus?: boolean;
};
export const BatchItem = ({
  expanded,
  item,
  onPlayComplete,
  disableFocus = false
}: BatchItemProps) => {
  const [css, theme] = useStyletron();
  const containerRef = useRef<HTMLDivElement>(null);
  const previousExpanded = usePreviousValue(expanded);
  useEffect(() => {
    if (containerRef.current && expanded && !disableFocus) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [expanded, disableFocus]);
  useEffect(() => {
    if (onPlayComplete && previousExpanded && !expanded && containerRef.current) {
      requestAnimationFrame(() => {
        onPlayComplete?.();
      });
    }
  }, [previousExpanded, expanded, onPlayComplete]);
  return <div className={css({
    display: 'flex',
    flexDirection: 'column',
    border: `2px solid ${theme.colors.borderOpaque}`,
    borderRadius: theme.borders.radius400
  })} ref={containerRef}>
      <ListItem overrides={{
      Root: {
        style: ({
          $theme
        }) => ({
          padding: 0,
          backgroundColor: expanded ? $theme.colors.backgroundLightAccent : 'transparent',
          borderTopLeftRadius: $theme.borders.radius300,
          borderTopRightRadius: $theme.borders.radius300
        })
      },
      Content: {
        style: ({
          $theme
        }) => ({
          border: 'none',
          margin: 0,
          padding: $theme.sizing.scale600
        })
      }
    }} endEnhancer={() => <BatchItemAction expanded={expanded} currentId={item.id} hidden={item.hidden} />}>
        <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.sizing.scale100
      })}>
          <LabelMedium margin={0}>
            <StatefulTooltip content={item.id} showArrow={true} placement={PLACEMENT.top}>
              {item.title}
            </StatefulTooltip>
          </LabelMedium>

          {item.description && <ParagraphSmall margin={0} color={theme.colors.contentSecondary}>
              {item.description}
            </ParagraphSmall>}
          {item.status && <span>
              <Tag kind={BATCH_ITEM_STATUS_STYLES[item.status.type].kind} size={TAG_SIZE.small} closeable={false} overrides={tagOverrides} hierarchy={BATCH_ITEM_STATUS_STYLES[item.status.type].hierarchy}>
                {item.status.text}
              </Tag>
            </span>}
        </div>
      </ListItem>
      {item.content && item.content.length > 0 && <section className={css({
      display: 'flex',
      flexDirection: 'column',
      maxHeight: expanded ? `${MAX_CONTENT_HEIGHT}px` : '0',
      overflow: 'auto',
      transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      padding: expanded ? `${theme.sizing.scale500} ${theme.sizing.scale600} ${theme.sizing.scale600}` : 0
    })}>
          {item.content.map((content, index) => {
        const contentValue = content.value.replace(/&nbsp;|&#160;|\u00A0/g, ' ');
        return <div key={content.label} className={css({
          display: 'flex',
          flexDirection: 'column',
          borderBottom: `1px solid ${theme.colors.borderOpaque}`,
          padding: `${theme.sizing.scale300} 0`,
          ...(index === item.content!.length - 1 && {
            borderBottom: 'none'
          }),
          gap: theme.sizing.scale300
        })}>
                <LabelXSmall margin={0}>{content.label}</LabelXSmall>
                <ParagraphXSmall margin={0} color={theme.colors.contentSecondary} className={css({
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            wordBreak: 'normal'
          })}>
                  {contentValue}
                </ParagraphXSmall>
              </div>;
      })}
        </section>}
    </div>;
};